import type { BrandId, BrandKit } from './brand.types'
import { contrastText, darken, lighten } from './colorUtils'

/** One brand colour; the lighter/darker steps are derived unless given. */
interface BaseColor {
  main: string
  dark?: string
  light?: string
  lighter?: string
}

export interface BrandKitInput {
  id: BrandId
  /** Display name — "Evolve Wellness". */
  name: string
  /** Identifier form without spaces — "EvolveWellness". For keys, ids, file names. */
  key: string

  /** Mark for light surfaces. */
  logo: string
  /** Mark for the brand-coloured surface. Defaults to `logo`. */
  logoOnBrand?: string
  logoOnBrandPlate?: boolean
  favicon: string

  colors: { primary: BaseColor; secondary: BaseColor }
  /** Defaults to a gradient of the primary colour. */
  brandSurface?: string

  typography?: BrandKit['typography']
  bannerCopy?: BrandKit['bannerCopy']
  storefronts?: BrandKit['storefronts']
}

const toColor = (c: BaseColor) => ({
  main: c.main,
  dark: c.dark ?? darken(c.main, 0.25),
  light: c.light ?? lighten(c.main, 0.35),
  lighter: c.lighter ?? lighten(c.main, 0.85),
  contrastText: contrastText(c.main),
})

/**
 * Builds a BrandKit from a name, a logo and one or two base colours, so adding
 * a brand is one small file under `brands/` plus an entry in `brandDetection.ts`
 * (see `brands/evolveWellness.ts`).
 */
export const createBrandKit = (input: BrandKitInput): BrandKit => {
  const primary = toColor(input.colors.primary)
  const secondary = toColor(input.colors.secondary)

  return {
    id: input.id,
    name: input.name,
    key: input.key,
    logo: input.logo,
    logoOnBrand: input.logoOnBrand ?? input.logo,
    logoOnBrandPlate: input.logoOnBrandPlate,
    favicon: input.favicon,
    palette: { primary, secondary },
    brandSurface:
      input.brandSurface ?? `linear-gradient(135deg, ${primary.dark} 0%, ${primary.main} 100%)`,
    typography: input.typography,
    bannerCopy: input.bannerCopy,
    storefronts: input.storefronts,
  }
}
